import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";

import { Grid, Typography, IconButton, Tooltip, SvgIcon, Avatar, Button } from "@material-ui/core";

import Loader from "common/Loader";
import AppConstants from "../constants";
import MapContainer from "../common/GoogleMapContainer";

import { useStyle } from "./locations.styles";
import { useSelector } from "react-redux";
import { AppState } from "../config/redux/reducers";
import { DriverLocation, DriverLocationsFilters } from "./redux/DriverLocationsSlice.type";
import { fetchDriverLocations, clearDriverLocations } from "./redux/DriverLocationsSlice";
import { ReactComponent as refreshIconNew } from "./../assets/icons/refresh-icon-new.svg";
import mapItemIcon from "./../assets/icons/timeline-area-icon.svg";
import { mapOptionStyles } from "../RoutePlanning/AllRoutes/utils/AllRouteConstant";
import SelectBox from "common/SelectBox";
import { TextConstants } from "constants/TextConstants";
import DriverLocationConstants, { getStatusBadgeColor, getStatusMarkerColor, getCountryMapConfig } from "constants/DriverLocationConstants";
import { MAP_MARKER_ICON_INTERFACE } from "./DriverLocationsInterface";
import { addNRPageAction } from "utils/newRelicUtils";
import AccessDenied from "common/AccessDenied";
import { getUsernameFromJWT } from "utils/helpers.utils";
import ConsignmentDetailsPopup from "./ConsignmentDetailsPopup";

const DriverLocationPage = () => {
  const dispatch = useDispatch();
  const classes = useStyle();

  const { countryCode, hubRecordListWithCodeAndName, hubRecordListWithLatLng, userFeatureConfigDetails } = useSelector(
    (state: AppState) => state.common
  );

  const {
    loading,
    driverLocations,
    driverLocationsLoading,
    driverLocationsError,
  } = useSelector((state: AppState) => state.driverLocations);

  const [selectedDriver, setSelectedDriver] = useState<DriverLocation | null>(null);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [mapZoom, setMapZoom] = useState<number>(10);
  const [showMarkers, setShowMarkers] = useState<boolean>(false);
  const [consignmentPopupOpen, setConsignmentPopupOpen] = useState<boolean>(false);
  const [selectedConsignmentId, setSelectedConsignmentId] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    return getStatusMarkerColor(status);
  };

  // Handle opening consignment details popup
  const handleOpenConsignmentDetails = (consignmentId?: string, driverId?: string) => {
    if (consignmentId) {
      setSelectedConsignmentId(consignmentId);
    }
    setConsignmentPopupOpen(true);
  };

  const handleCloseConsignmentDetails = () => {
    setConsignmentPopupOpen(false);
    setSelectedConsignmentId(null);
  };

  // Get default country-based map configuration
  const getDefaultMapConfig = () => {
    const currentCountryCode = sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode;
    return getCountryMapConfig(currentCountryCode);
  };

  // Calculate map center based on selected hub (only when hub is selected)
  const calculateMapCenter = () => {
    const currentCountryCode = sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode;
    const hubLatLngList = hubRecordListWithLatLng[currentCountryCode] || [];
    const selectedHub = hubLatLngList.find((hub: any) => hub.storeCode === dashboardHub);
    if (selectedHub && dashboardHub !== 'all') {
      if (selectedHub.latitude && selectedHub.longitude) {
        const center = {
          lat: parseFloat(selectedHub.latitude),
          lng: parseFloat(selectedHub.longitude)
        };
        return center;
      }
    }
    
    // Return country-based default when no specific hub is selected
    return getDefaultMapConfig().center;
  };

  // Calculate optimal zoom level based on driver spread
  const calculateZoomLevel = (drivers: DriverLocation[]) => {
    const validDrivers = drivers.filter(driver => 
      driver.location?.latitude && driver.location?.longitude &&
      (dashboardDriverStatus.length === 0 || dashboardDriverStatus.includes(driver.status))
    );
    
    if (validDrivers.length <= 1) return 15; // Closer zoom for single driver
    
    const lats = validDrivers.map(driver => driver.location!.latitude);
    const lngs = validDrivers.map(driver => driver.location!.longitude);
    
    const latSpread = Math.max(...lats) - Math.min(...lats);
    const lngSpread = Math.max(...lngs) - Math.min(...lngs);
    const maxSpread = Math.max(latSpread, lngSpread);
    
    // More aggressive zoom levels for better focus
    if (maxSpread > 0.5) return 10;   // Very wide spread
    if (maxSpread > 0.2) return 11;   // Wide spread  
    if (maxSpread > 0.1) return 12;   // Medium spread
    if (maxSpread > 0.05) return 13;  // Small spread
    if (maxSpread > 0.02) return 14;  // Very small spread
    if (maxSpread > 0.01) return 15;  // Tight cluster
    return 16; // Very tight cluster
  };

  const [driverLocationFilters, setDriverLocationFilters] = useState<DriverLocationsFilters>({
    hub: '', // Start with empty hub - only call API when hub is actually selected
    deliveryType: DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_DELIVERY_TYPE,
    metadata: 'true'
  });

  const [dashboardHub, setDashboardHub] = useState('all');
  const [dashboardDeliveryType, setDashboardDeliveryType] = useState(DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_DELIVERY_TYPE);
  const [dashboardDriverStatus, setDashboardDriverStatus] = useState<string[]>(DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_SELECTED_STATUSES);

  // Initialize map with country-based defaults on component mount
  useEffect(() => {
    const defaultConfig = getDefaultMapConfig();
    setMapCenter(defaultConfig.center);
    setMapZoom(defaultConfig.zoom);
  }, [countryCode]); // Re-initialize when country changes

  // Clear driver locations and reset states when country changes
  useEffect(() => {
    dispatch(clearDriverLocations());
    setSelectedDriver(null);
    setDashboardHub('all');
    setDashboardDeliveryType(DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_DELIVERY_TYPE);
    setDashboardDriverStatus(DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_SELECTED_STATUSES);
    setDriverLocationFilters({
      hub: '',
      deliveryType: DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_DELIVERY_TYPE,
      metadata: 'true'
    });
  }, [countryCode, dispatch]);

  // Update map and show markers only when filters are applied
  useEffect(() => {
    const hasFiltersApplied = dashboardHub !== 'all' || 
                            dashboardDeliveryType !== 'all' || 
                            dashboardDriverStatus.length !== DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DEFAULT_SELECTED_STATUSES.length;
    
    setShowMarkers(hasFiltersApplied && driverLocations.length > 0);
    
    if (hasFiltersApplied && driverLocations.length > 0) {
      const newCenter = calculateMapCenter();
      setMapCenter(newCenter);
      const newZoom = calculateZoomLevel(driverLocations);
      setMapZoom(newZoom);
    } else {
      // Reset to country default when no filters applied
      const defaultConfig = getDefaultMapConfig();
      setMapCenter(defaultConfig.center);
      setMapZoom(defaultConfig.zoom);
    }
  }, [dashboardHub, dashboardDeliveryType, dashboardDriverStatus, driverLocations, countryCode]);

  // Handle hub change for SelectBox component
  const handleHubChange = useCallback((value: string) => {
    addNRPageAction(AppConstants.NR_FEATURES.DRIVER.NAME, AppConstants.NR_FEATURES.DRIVER.FEATURES.LIVE_DRIVER);

    setDashboardHub(value);
    // Clear selected driver when hub changes
    setSelectedDriver(null);
    // Clear existing driver locations when hub changes
    dispatch(clearDriverLocations());
    const updatedFilters = {
      ...driverLocationFilters,
      hub: value
    };
    setDriverLocationFilters(updatedFilters);
  }, [driverLocationFilters, dispatch]);

  // Handle delivery type change for SelectBox component
  const handleDeliveryTypeChange = useCallback((value: string) => {
    setDashboardDeliveryType(value);
    // Clear selected driver when delivery type changes
    setSelectedDriver(null);
    const updatedFilters = {
      ...driverLocationFilters,
      deliveryType: value
    };
    setDriverLocationFilters(updatedFilters);
  }, [driverLocationFilters]);

  // Handle driver status change for SelectBox component (multi-select)
  const handleDriverStatusChange = useCallback((value: string[]) => {
    setDashboardDriverStatus(value);
    // Clear selected driver when driver status filter changes
    setSelectedDriver(null);
  }, []);

  const handleRefreshDriverLocations = useCallback(() => {
    const driverLocationFilters: DriverLocationsFilters = {
      hub: dashboardHub !== 'all' ? dashboardHub : '',
      deliveryType: dashboardDeliveryType !== 'all' ? dashboardDeliveryType : '',
      metadata: 'true'
    };
    dispatch(fetchDriverLocations(driverLocationFilters));
  }, [dashboardHub, dashboardDeliveryType, dispatch]);

  useEffect(() => {
    let autoReloadInterval: ReturnType<typeof setInterval> | undefined;
    
    if (driverLocations && driverLocations.length > 0 && !driverLocationsLoading) {
      autoReloadInterval = setInterval(() => {
        handleRefreshDriverLocations();
      }, 60000); // 60 seconds
    }
    
    return () => {
      if (autoReloadInterval) {
        clearInterval(autoReloadInterval);
      }
    };
  }, [driverLocations, driverLocationsLoading, handleRefreshDriverLocations]);

  const createDriverMarkerIcon = useCallback((color: string): MAP_MARKER_ICON_INTERFACE => {
    const coloredPulseSvg = `
      <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
        <circle cx="30" cy="30" r="8" fill="${color}">
          <animate attributeName="r" from="8" to="20" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" from="1" to="0" dur="1.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="30" cy="30" r="6" fill="${color}" stroke="white" stroke-width="2"/>
      </svg>
    `;

    // Return using proper Google Maps marker icon format with fixed anchor
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(coloredPulseSvg)}`,
      scaledSize: { width: 60, height: 60 },
      anchor: { x: 30, y: 30 },
      labelOrigin: { x: 30, y: 30 }
    };
  }, []);

  const getHubMarkerIcon = useCallback(() => {
    return mapItemIcon;
  }, []);

  // Get hub markers when a specific hub is selected
  const getHubMarkers = () => {
    if (dashboardHub === 'all' || !dashboardHub) {
      return [];
    }

    const currentCountryCode = sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode;
    const hubLatLngList = hubRecordListWithLatLng[currentCountryCode] || [];
    const selectedHub = hubLatLngList.find((hub: any) => hub.storeCode === dashboardHub);

    if (!selectedHub || !selectedHub.latitude || !selectedHub.longitude) {
      return [];
    }

    const hubIcon = getHubMarkerIcon();
    
    return [{
      name: selectedHub.storeName || 'Hub',
      location: {
        lat: parseFloat(selectedHub.latitude),
        lng: parseFloat(selectedHub.longitude)
      },
      icon: hubIcon,
      // Hub marker data for info window
      isHub: true,
      hubCode: selectedHub.storeCode,
      hubName: selectedHub.storeName,
      hubLatitude: selectedHub.latitude,
      hubLongitude: selectedHub.longitude
    }];
  };

  const getMapMarkers = () => {
    // Always show hub markers when a specific hub is selected
    const hubMarkers = getHubMarkers();
    
    // Only show driver markers if filters are applied and showMarkers is true
    if (!showMarkers) {
      return hubMarkers;
    }
    
    const driverMarkers = driverLocations
      .filter((driver: DriverLocation) => {
        const hasValidLocation = driver.location?.latitude && driver.location?.longitude;
        const matchesSelectedStatus = dashboardDriverStatus.length === 0 || 
          dashboardDriverStatus.includes(driver.status);
        return hasValidLocation && matchesSelectedStatus;
      })
      .map((driver: DriverLocation, index: number) => {
        const statusColor = getStatusColor(driver.status);
        const coloredPulseIcon = createDriverMarkerIcon(statusColor);
        
        return {
          name: driver.driverName,
          location: {
            lat: Number(driver.location!.latitude),
            lng: Number(driver.location!.longitude)
          },
          icon: coloredPulseIcon,
          // Store additional driver data for info window
          driverId: driver.driverId,
          driverName: driver.driverName,
          driverStatus: driver.status,
          battery: (driver as any).batteryLevel?.toString() || '0',
          charging: (driver as any).batteryIsCharging ? 'Yes' : 'No',
          vehicle: driver.vehicleType,
          assignments: driver.assignments?.toString() || '0'
        };
      });

    // Combine hub and driver markers
    return [...hubMarkers, ...driverMarkers];
  };

  // Function to get marker details for InfoWindow (handles both drivers and hubs)
  const getMarkerDetailsForInfoWindow = (marker: any) => {
    // Handle hub markers
    if (marker.isHub) {
      return (
        <div className={classes.hubInfoWindow}>
          <div className={classes.hubInfoHeader}>
            <div>
              <div className={classes.hubInfoTitle}>
                {marker.hubName || 'Hub'} ({marker.hubCode})
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Handle driver markers - use setTimeout to defer state update
    setTimeout(() => {
      const driver = driverLocations.find((d: DriverLocation) => d.driverId === marker.driverId);
      if (driver && (!selectedDriver || selectedDriver.driverId !== driver.driverId)) {
        setSelectedDriver(driver);
      }
    }, 0);
    
    return (
      <div style={{ display: 'none' }}>
      </div>
    );
  };

  // Function to get battery color based on percentage
  const getBatteryColor = (percentage: number) => {
    if (percentage > 60) return DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.BATTERY_COLORS.GREEN;
    if (percentage > 30) return DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.BATTERY_COLORS.ORANGE;
    return DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.BATTERY_COLORS.RED;
  };

  // Driver Details Card Component
  const renderDriverDetailsCard = () => {
    if (!selectedDriver) return null;

    const statusColors = getStatusBadgeColor(selectedDriver.status);
    const batteryPercentage = Math.round((selectedDriver.batteryLevel || 0) * 100);
    const batteryColor = getBatteryColor(batteryPercentage);

    return (
      <div className={classes.driverDetailsCard}>
        <IconButton 
          className={classes.closeButton}
          onClick={() => setSelectedDriver(null)}
          size="small"
        >
          ×
        </IconButton>
        
        <div className={classes.driverDetailsHeader}>
          <Avatar 
            className={classes.driverAvatar}
            src={(selectedDriver as any).images?.lg || ''}
            alt={selectedDriver.driverName || 'Driver'}
          >
            {!((selectedDriver as any).images?.lg) && (selectedDriver.driverName || 'D')[0].toUpperCase()}
          </Avatar>
          <div className={classes.driverNameContainer}>
            <Typography className={classes.driverDetailsTitle}>
              {selectedDriver.driverName || 'Unknown Driver'}
            </Typography>
          </div>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.STATUS}</span>
          <span 
            className={classes.statusBadge}
            style={{ 
              backgroundColor: statusColors.bg, 
              color: statusColors.color 
            }}
          >
            {selectedDriver.status?.replace(/_/g, ' ')}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.VEHICLE}</span>
          <span className={classes.driverDetailsValue}>
            {selectedDriver.vehicleType || 'BIKE'}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.CONTACT}</span>
          <span className={classes.driverDetailsValue}>
            {selectedDriver.driverPhoneNumber ? (
              <a href={`tel:${selectedDriver.driverPhoneNumber}`}>
                {selectedDriver.driverPhoneNumber}
              </a>
            ) : (
              'N/A'
            )}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.ASSIGNMENTS}</span>
          <div className={classes.driverDetailsValue}>
            {selectedDriver.consignments && selectedDriver.consignments.length > 0 ? (
              <div className={classes.consignmentContainer}>
                {selectedDriver.consignments.map((consignmentId, index) => (
                  <span
                    key={`${consignmentId}-${index}`}
                    className={classes.consignmentBadge}
                    onClick={() => handleOpenConsignmentDetails(consignmentId, selectedDriver.driverId)}
                    onKeyPress={(e) => { if (e.key === 'Enter') handleOpenConsignmentDetails(consignmentId, selectedDriver.driverId); }}
                    tabIndex={0}
                  >
                    {consignmentId}
                  </span>
                ))}
              </div>
            ) : (
              <span className={classes.noAssignments}>No assignments</span>
            )}
          </div>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.DISTANCE_TO_HUB}</span>
          <span className={classes.driverDetailsValue}>
            {(selectedDriver as any).proximityToHub ? `${parseFloat((selectedDriver as any).proximityToHub).toFixed(2)} km` : 'N/A'}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.SPEED}</span>
          <span className={classes.driverDetailsValue}>
            {selectedDriver.speed ? `${selectedDriver.speed} km/h` : '0 km/h'}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.BATTERY}</span>
          <div className={classes.batteryContainer}>
            <div className={classes.batteryBar}>
              <div 
                className={classes.batteryFill}
                style={{ 
                  width: `${batteryPercentage}%`, 
                  backgroundColor: batteryColor 
                }}
              />
            </div>
            <span className={classes.driverDetailsValue}>
            {batteryPercentage}% {selectedDriver.batteryIsCharging ? '⚡' : ''}
            </span>
          </div>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.ACTIVITY}</span>
          <span className={classes.driverDetailsValue}>
            {(selectedDriver as any).activityType || 'still'}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.CHECK_IN}</span>
          <span className={classes.driverDetailsValue}>
            {(selectedDriver as any).hubCheckInTime ? new Date((selectedDriver as any).hubCheckInTime).toLocaleString() : 'N/A'}
          </span>
        </div>

        <div className={classes.driverDetailsRow}>
          <span className={classes.driverDetailsLabel}>{DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_DETAIL_LABELS.LOCATION}</span>
          <span className={classes.driverDetailsValue}>
            {selectedDriver.location?.latitude?.toFixed(6)}, {selectedDriver.location?.longitude?.toFixed(6)}
          </span>
        </div>
      </div>
    );
  };

  // Fetch driver locations only when a valid hub is selected
  useEffect(() => {
    if (driverLocationFilters.hub && driverLocationFilters.hub !== '' && driverLocationFilters.hub !== 'all') {
      dispatch(fetchDriverLocations(driverLocationFilters));
    }
  }, [dispatch, driverLocationFilters]);

  return (
    <div className={classes.root}>
      {loading && <Loader></Loader>}  
      <Typography variant="h6" gutterBottom className={classes.title}>
        {TextConstants.DRIVER_LOCATION_PG_TITLE}
      </Typography>
      {/* Full height map container with overlay controls */}
      <div className={classes.mapOverlayContainer}>
        {/* Filter controls and legend as overlay */}
        <div className={classes.mapOverlayControls}>
          <Grid className={classes.container} container spacing={0}>
            <Grid item xs={12}>
              {/* Driver Location Filters - Horizontal Layout */}
              <Grid container spacing={2} className={classes.filtersContainer}>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <SelectBox
                    fullWidth
                    handleChange={handleHubChange}
                    value={dashboardHub}
                    label="Hub"
                    enableSearch={true}
                    id="dashboard-hub-select-box"
                    items={
                      hubRecordListWithCodeAndName[
                        sessionStorage.getItem(AppConstants.COUNTRY_CODE) ||
                          countryCode
                      ] || []
                    }
                  ></SelectBox>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <SelectBox
                    fullWidth
                    handleChange={handleDeliveryTypeChange}
                    value={dashboardDeliveryType}
                    label="Delivery Type"
                    enableSearch={false}
                    id="dashboard-delivery-type-select-box"
                    items={DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DELIVERY_TYPE_OPTIONS}
                  ></SelectBox>
                </Grid>
                <Grid item xs={12} sm={6} md={2} lg={2}>
                  <SelectBox
                    fullWidth
                    multiple={true}
                    handleChange={handleDriverStatusChange}
                    value={dashboardDriverStatus}
                    label="Driver Status"
                    enableSearch={true}
                    id="dashboard-driver-status-select-box"
                    items={DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.DRIVER_STATUS_OPTIONS}
                  ></SelectBox>
                </Grid>
                {driverLocations.length > 0 && (<Grid item xs={12} sm={6} md={1} lg={1} className={classes.refreshIconContainer}>
                  <Tooltip title="Refresh Driver Locations">
                    <SvgIcon
                      className={classes.refreshIcon}
                      onClick={handleRefreshDriverLocations}
                      component={refreshIconNew}
                    />
                  </Tooltip>
                </Grid>)}
              </Grid>
              
              {/* Status Legend and Driver Count - Vertical Layout */}
              <Grid container className={classes.legendContainer}>
                <Grid item xs={12}>
                  {/* Status Legend */}
                  <div className={classes.statusLegend}>
                    {Object.entries(DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.STATUS_COLORS).map(([status, color]) => {
                      const statusCount = driverLocations.filter((driver: DriverLocation) => driver.status === status).length;
                      return (
                        <div key={status} className={classes.statusLegendItem}>
                          <div 
                            className={classes.statusCircle} 
                            style={{ backgroundColor: getStatusMarkerColor(status) }}
                          ></div>
                          <span className={classes.statusLabel}>
                            {status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())} ({statusCount})
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" className={classes.driverCountText}>
                    Showing {driverLocations
                      .filter((driver: DriverLocation) => {
                        const hasValidLocation = driver.location?.latitude && driver.location?.longitude;
                        const matchesSelectedStatus = dashboardDriverStatus.length === 0 || 
                          dashboardDriverStatus.includes(driver.status);
                        return hasValidLocation && matchesSelectedStatus;
                      }).length} of {driverLocations.length} drivers
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
        
        {/* Full height map */}
        <div className={classes.mapFullHeight}>
            <div className={classes.mapWrapper}>
              {driverLocationsLoading && (
                <div className={classes.mapLoadingOverlay}>
                  <Loader />
                </div>
              )}
              
              {/* Always show map, but with different content based on filters */}
              <MapContainer
                key={`map-${driverLocationFilters.hub}-${dashboardDriverStatus.join('-')}-${countryCode}`}
                mapOptionStyles={mapOptionStyles}
                markers={getMapMarkers()}
                center={mapCenter || getDefaultMapConfig().center}
                zoom={mapZoom}
                updateMap={true}
                showInfoWindow={true}
                getRouteDetailsForInfoWindow={getMarkerDetailsForInfoWindow}
              />
              
              {/* Show message overlay when no filters are applied */}
              {!showMarkers && (
                <div className={classes.messageOverlay}>
                  <Typography variant="h6" className={classes.messageOverlayTitle}>
                    {getDefaultMapConfig().name}
                  </Typography>
                  <Typography variant="body2" className={classes.messageOverlayText}>
                    {DriverLocationConstants.DRIVER_LOCATION_CONSTANTS.MAP_OVERLAY_DEFAULT_MESSAGE}
                  </Typography>
                </div>
              )}
              
              {/* Show error message if there's an error */}
              {driverLocationsError && (
                <div className={classes.errorMessage}>
                  Error: {driverLocationsError}
                </div>
              )}
              
              {/* Driver Details Card Overlay */}
              {renderDriverDetailsCard()}
            </div>
          </div>
        </div>

        {selectedConsignmentId && (
          <ConsignmentDetailsPopup
            open={consignmentPopupOpen}
            onClose={handleCloseConsignmentDetails}
            consignmentId={selectedConsignmentId}
          />
        )}
      </div>
  );
};

export default React.memo(DriverLocationPage);